/**
 * The personal, first-person easter-egg page. Ports legacy /hi
 * (docs/parity/auth-public.md §2.11): public, fully static, reachable with
 * or without a session, regardless of onboarding state.
 *
 * The source text (legacy/templates/hi.html:93-146) uses seven em dashes.
 * Per CLAUDE.md "Em dashes" (a dash only where it does a real job, and this
 * first-person note most sparingly of all, since it is where "reads as AI"
 * came from) two are kept: the "— Alexis" signature, and the aside after
 * "Photography", where commas would turn "Photography" into one more item
 * of the list it introduces. The other five are a comma, colon or full
 * stop. Tone and structure are otherwise reproduced faithfully, per the
 * parity spec's own instruction for this page ("reproduce the
 * tone/structure, not a byte-for-byte transcription").
 *
 * Stone & ink (hi.css): the letter sits on the rail's ink, in the app's own
 * type, with the Mono eyebrow. Every colour is a role token with at least
 * 4.5:1 on the ink (the legacy greys were 1.9 to 3:1). The page is kept out
 * of search results (noindex, and not in sitemap.xml): an easter egg that
 * turns up in a search is not one any more.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { useDocumentMeta } from '@/features/site/useDocumentMeta';
import './hi.css';

export const Route = createFileRoute('/hi')({
  component: Hi,
});

function Hi() {
  useDocumentMeta({
    title: 'Hi',
    description: 'A note from Alexis Petit, the chef who builds Pleach.',
    noindex: true,
  });
  return (
    <div className="hi-page" data-pl-dark="">
      <main id="main" className="hi-wrap">
        <p className="hi-label">:: you found it ::</p>

        <div className="hi-story">
          <h1 className="hi-lead">Oh hi! You got here somehow.</h1>

          <p>
            Thanks for using our solution.
            <br />
            Well, I say <em>our</em>, but... it&apos;s just me, actually.
          </p>

          <p>
            My name is <strong>Alexis Petit</strong>. I&apos;ve been a chef for over a decade. Always
            passionate about tech, solutions, problems... and the arts, amongst other things. A lot of
            things, honestly. I have a hard time keeping still.
          </p>

          <p>
            I made my career from the bottom of the ladder. Prep cook, commis, chef de partie, all of it.
            Years of service, managing kitchens, looking after teams, doing an almost offensive amount of
            spreadsheets at midnight before a busy Saturday. Admin I secretly loved. Don&apos;t tell
            anyone.
          </p>

          <p>
            Along the way I helped open a few places. With my mate Kélian, we signed a lease on an old
            post office on Karangahape Road and turned it into <strong>Atelier</strong>. That one we
            built from scratch. Proud of that one.
          </p>

          <p>
            On the side I shoot. Photography — people, events, food, whatever catches my eye. You&apos;ll
            find that under askipar if you&apos;re curious.
          </p>

          <p>
            Anyway. One day, staring at yet another spreadsheet, I thought: <em>what if I just... turned
            my recipe book into an app?</em>
          </p>

          <p>
            I looked around. Couldn&apos;t find anything that actually solved the problem. Or at least
            not something that didn&apos;t cost hundreds of dollars a month and still somehow miss half
            of what a real kitchen needs.
          </p>

          <p>
            So I said <strong>fuck it</strong>. Let&apos;s build it.
          </p>

          <p>
            What started as a simple place to list ingredients and recipes turned into something that
            never stopped growing. The allergen tracking came from a near-miss I don&apos;t like thinking
            about. The COGS calculator because I was sick of doing food cost on the back of a docket at
            1am. The unit conversions because scaling a recipe from 4 portions to 85 covers is a very
            special kind of hell that only chefs understand.
          </p>

          <p>
            I&apos;m not a developer. No computer science degree. No formal training. Just a chef who got
            very annoyed at the gap between what kitchens actually need and what software bothers to
            provide. That annoyance turned out to be useful.
          </p>

          <p>
            Pleach is the tool I wish I&apos;d had on day one of running a kitchen. Every single feature
            came from a real problem, a real shift, a real <em>&quot;why doesn&apos;t something just do
            this?&quot;</em> moment.
          </p>

          <p>
            It&apos;s still growing. There&apos;s always something to add, something to fix, something
            the industry needs that nobody&apos;s thought to build yet. That&apos;s what keeps it
            interesting.
          </p>

          <p>If you&apos;re using it, genuinely, thank you. It means more than you&apos;d think.</p>

          <p>And if something&apos;s not working, or you have an idea... well. You know where the Feedback tab is. :)</p>
        </div>

        <div className="hi-sig">
          <span>— Alexis</span>
          <Link to="/" className="hi-back">
            ← back to the app
          </Link>
        </div>
      </main>
    </div>
  );
}
